import {
  AutoAppliedFilterType,
  AutoAppliedFilters,
  SearchFilters,
} from "@outschool/gql-backend-generated";
import {
  SearchListingsQuery,
  SearchListingsQueryVariables,
} from "@outschool/gql-frontend-generated";
import { QueryHookOptions, QueryResult } from "@outschool/ui-apollo";
import { useExpiringValue } from "@outschool/ui-utils";
import { camelCase, isNumber, random, uniqBy } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import usePaginatedQuery from "../hooks/usePaginatedQuery";
import searchListingsQuery from "./graphql/searchListingsQuery";

import { useTrackEvent } from "@outschool/ui-analytics";
import isEqual from "lodash/isEqual";
import mapKeys from "lodash/mapKeys";

/**
 * Creates a function that tracks when new search filters are applied.
 */
function useTrackSearchFiltersApplied() {
  const trackEvent = useTrackEvent();

  const trackSearchFiltersApplied = useCallback(
    ({
      filterCategory,
      oldFilters,
      newFilters,
      searchUid,
      oldSearchUid,
    }: {
      filterCategory?: string;
      oldFilters: SearchFilters;
      newFilters: SearchFilters;
      searchUid: string;
      oldSearchUid?: string;
    }) => {
      if (!isEqual(oldFilters, newFilters)) {
        trackEvent("Search filters applied", {
          filterCategory,
          ...mapKeys(oldFilters, (_value, key) => `old_${key}`),
          ...mapKeys(newFilters, (_value, key) => `new_${key}`),
          old_searchUid: oldSearchUid,
          new_searchUid: searchUid,
        });
        return searchUid;
      }
      return searchUid;
    },
    [trackEvent],
  );

  return {
    trackSearchFiltersApplied,
  };
}

export type DisableAutoApplyFilters = (
  disableFilters: AutoAppliedFilterType[],
) => void;

export function useSearchQuery(
  options: Omit<
    QueryHookOptions<SearchListingsQuery, SearchListingsQueryVariables> & {
      includeMatchingSections?: boolean;
      source?: boolean;
      referrer?: string;
    },
    "searchUid"
  >,
  skipOtherListings: boolean = false,
) {
  const [disabledAutoApplyFilters, setDisabledAutoApplyFilters] = useState<
    AutoAppliedFilterType[]
  >([]);
  const getItems = useCallback((result: SearchListingsQuery) => {
    const esResult = result as SearchListingsQuery;
    return esResult?.searchListings?.listings ?? [];
  }, []);
  const getResult = useCallback(
    (
      previousResult: SearchListingsQuery,
      fetchMoreResult: SearchListingsQuery,
      items: SearchListingsQuery["searchListings"]["listings"],
    ) => {
      const uniqueItems = uniqBy(items, listing => listing.activity.uid);
      const esPrev = previousResult as SearchListingsQuery;
      const esNext = fetchMoreResult as SearchListingsQuery;

      return {
        ...previousResult,
        ...fetchMoreResult,
        searchListings: {
          ...esPrev.searchListings,
          ...esNext.searchListings,
          listings: uniqueItems,
        },
      };
    },
    [],
  );
  const getFetchMoreVariables = useCallback(
    (
      result: QueryResult<SearchListingsQuery, SearchListingsQueryVariables>,
    ) => {
      const esResult = result as QueryResult<
        SearchListingsQuery,
        SearchListingsQueryVariables
      >;
      return {
        cursor: esResult.data?.searchListings.cursor,
        // @ts-ignore TS(2345): Argument of type 'SearchListingsQuery | undefined'... Remove this comment to see the full error message
        offset: getItems(result.data).length,

        // When fetching more listings, we want to pass the same search UID as the previous result
        // so that paginated results are associated with the same search.
        searchUid: esResult.data?.searchListings.searchUid,
      };
    },
    [getItems],
  );

  const hasMoreItems = useCallback(
    (data: SearchListingsQuery) => {
      const esResult = data as SearchListingsQuery;
      return (
        // If there is a cursor, there are more items
        Boolean(esResult?.searchListings?.cursor) ||
        // If there are items, and the total is greater than the offset plus the limit, there are more items
        (getItems(data).length > 0 &&
          // @ts-ignore TS(2531): Object is possibly 'null'.
          esResult?.searchListings?.total >
            // @ts-ignore TS(2533): Object is possibly 'null' or 'undefined'.
            options.variables?.offset + options.variables.limit)
      );
    },
    [
      getItems,
      options.variables?.offset,
      // @ts-ignore TS(2532): Object is possibly 'undefined'.
      options.variables.limit,
    ],
  );

  // The backend needs a seed for randomization purposes on no query searches
  const [seed] = useExpiringValue(
    "randomized_search_key",
    random(1000000),
    30 * 60 * 1000,
  );

  const searchFilters = useMemo(
    () => ({
      ...options.variables?.searchFilters,
      /**
       *
       * Added a check to allow for querying unavailable classes for the
       * use case of outschool subscriptions where self-paced classes
       * have no sections and the classes aren't open for enrollments.
       *
       */
      ...("undefined" === typeof options.variables?.searchFilters?.isAvailable
        ? { isAvailable: true }
        : {}),
    }),
    [options.variables?.searchFilters],
  );

  const exactResult = usePaginatedQuery(
    searchListingsQuery,
    {
      ...options,
      variables: {
        ...options.variables!,
        searchFilters: {
          ...searchFilters,
          seed,
        },
        referrer: options.referrer,
        includeMatchingSections: options.includeMatchingSections,
        source: options.source,
        disableAutoApplyFilterTypes: disabledAutoApplyFilters,
      },
    },
    getItems,
    getResult,
    getFetchMoreVariables,
    hasMoreItems,
  );

  // Unavailable classes query
  const otherResult = usePaginatedQuery(
    searchListingsQuery,
    {
      ...options,
      variables: {
        ...options.variables!,
        searchFilters: {
          ...searchFilters,
          isAvailable: false,
        },
        disableAutoApplyFilterTypes: disabledAutoApplyFilters,
      },
      skip:
        skipOtherListings ||
        exactResult?.data === undefined ||
        exactResult?.loading ||
        (exactResult?.hasMore ?? true),
    },
    getItems,
    getResult,
    getFetchMoreVariables,
    hasMoreItems,
  );

  // Track when search filters change and log the old/new search filters/search UID.
  const { trackSearchFiltersApplied } = useTrackSearchFiltersApplied();
  const previousFiltersRef = useRef(searchFilters);
  const previousSearchUidRef = useRef(
    exactResult.data?.searchListings?.searchUid,
  );
  useEffect(() => {
    // Fire whenever new results are loaded and filters have changed.
    if (!exactResult.loading && exactResult.data) {
      const searchUid = exactResult.data.searchListings?.searchUid;

      if (
        searchFilters &&
        previousFiltersRef.current &&
        !isEqual(previousFiltersRef.current, searchFilters)
      ) {
        trackSearchFiltersApplied({
          oldFilters: previousFiltersRef.current,
          newFilters: searchFilters,
          searchUid: exactResult.data.searchListings?.searchUid,
          oldSearchUid: previousSearchUidRef.current,
        });
      }

      // Always update the previous filters and search UID.
      previousFiltersRef.current = searchFilters;
      previousSearchUidRef.current = searchUid;
    }
  }, [
    exactResult.loading,
    exactResult.data,
    searchFilters,
    trackSearchFiltersApplied,
  ]);

  const fetchMore = useMemo(
    () =>
      exactResult?.hasMore
        ? exactResult?.fetchMore
        : otherResult?.hasMore
        ? otherResult?.fetchMore
        : () => {},
    [
      exactResult?.hasMore,
      exactResult?.fetchMore,
      otherResult?.hasMore,
      otherResult?.fetchMore,
    ],
  );

  const getTotalFromResult = (result: Pick<QueryResult, "data">): number => {
    const total = result?.data?.searchListings?.total;

    if (isNumber(total)) {
      return total;
    }

    // @ts-ignore TS(2322): Type 'null' is not assignable to type 'number'.
    return null;
  };

  return {
    autoAppliedFilters:
      exactResult?.data?.searchListings?.autoAppliedFilters ?? {},
    // @ts-ignore TS(2345): Argument of type '{ searchListings: { listings: { ... Remove this comment to see the full error message
    exactListings: exactResult.loading ? [] : getItems(exactResult.data),
    exactTotal: getTotalFromResult(exactResult),
    // @ts-ignore TS(2345): Argument of type '{ searchListings: { listings: { ... Remove this comment to see the full error message
    otherListings: otherResult.loading ? [] : getItems(otherResult.data),
    otherTotal: getTotalFromResult(otherResult),
    loading: exactResult?.loading ?? false,
    hasMore: (exactResult?.hasMore || otherResult?.hasMore) ?? true,
    exactHasMore: exactResult?.hasMore ?? true,
    isFetchingMore:
      (exactResult?.isFetchingMore ||
        otherResult?.loading ||
        otherResult?.isFetchingMore) ??
      false,
    fetchMore,
    error: exactResult?.error ?? otherResult?.error,
    setDisabledAutoApplyFilters,
    disabledAutoApplyFilters,
    exactListingsSearchUid: exactResult.loading
      ? undefined
      : exactResult?.data?.searchListings?.searchUid,
    otherListingsSearchUid: otherResult.loading
      ? undefined
      : otherResult?.data?.searchListings?.searchUid,
  };
}

export function setDisabledAutoApplyFilter({
  disableFilterType,
  autoAppliedFilters,
  disabledAutoApplyFilters,
  setDisabledAutoApplyFilters,
}: {
  disableFilterType: AutoAppliedFilterType;
  autoAppliedFilters: AutoAppliedFilters;
  disabledAutoApplyFilters: AutoAppliedFilterType[];
  setDisabledAutoApplyFilters: DisableAutoApplyFilters;
}) {
  const disableFilterTypeKey = camelCase(disableFilterType);
  // are auto applied filters active for this filter type?
  const hasAutoAppliedFilters =
    autoAppliedFilters?.[disableFilterTypeKey as keyof AutoAppliedFilters]
      ?.length &&
    (autoAppliedFilters?.[disableFilterTypeKey as keyof AutoAppliedFilters]
      ?.length ?? 0) > 0;
  // is this auto appplied filter enabled?
  const canFilterTypeBeDisabled =
    !disabledAutoApplyFilters?.includes(disableFilterType);
  if (
    setDisabledAutoApplyFilters &&
    hasAutoAppliedFilters &&
    canFilterTypeBeDisabled
  ) {
    setDisabledAutoApplyFilters([
      ...disabledAutoApplyFilters,
      disableFilterType,
    ]);
  }
}
